/**
 * What a new row starts with, in one view.
 *
 * A default belongs to the *view* rather than to the field, which is what makes
 * it worth having: a view filtered to one stage of a pipeline can start every
 * row it creates in that stage, without the column meaning that anywhere else.
 *
 * Two ways to say what the default is, and they are exclusive. A **value** is
 * stored as it stands. A **function** is resolved when the row is made, so a
 * date defaulted to `now` is the moment of creation rather than the moment
 * somebody set the default -- which is the only one of the two that stays right.
 */
import type { Field, FieldTypeName, ViewDefaultValue } from '@/api/types'

/**
 * The functions the backend can resolve at row-creation time.
 *
 * A union rather than a string, so that a name added here has to be given a
 * label before it compiles -- an unnamed choice in the list would be a mode
 * somebody can select and cannot read.
 */
export type DefaultValueFunction = 'now'

/**
 * Which functions each field type resolves, which is the backend's own list.
 *
 * Exhaustive, so a twenty-eighth field type has to answer -- and the answer for
 * almost all of them is none: `get_supported_default_value_functions` is
 * overridden on exactly one type.
 */
export const DEFAULT_VALUE_FUNCTIONS = {
  date: ['now'],
  text: [],
  long_text: [],
  url: [],
  email: [],
  phone_number: [],
  uuid: [],
  number: [],
  rating: [],
  boolean: [],
  duration: [],
  created_on: [],
  last_modified: [],
  created_by: [],
  last_modified_by: [],
  autonumber: [],
  formula: [],
  count: [],
  rollup: [],
  lookup: [],
  file: [],
  single_select: [],
  multiple_select: [],
  link_row: [],
  multiple_collaborators: [],
  password: [],
  form_view_edit_row: [],
} as const satisfies Record<FieldTypeName, readonly DefaultValueFunction[]>

export function defaultValueFunctions(field: Field): readonly DefaultValueFunction[] {
  return DEFAULT_VALUE_FUNCTIONS[field.type]
}

/** What the dialog holds for one column while it is being edited. */
export interface DefaultValueDraft {
  fieldId: number
  enabled: boolean
  /** The editor's draft string, used only when no function is chosen. */
  draft: string
  /** The function chosen, or null for a stored value. */
  function: DefaultValueFunction | null
}

/**
 * What a view already says, as drafts.
 *
 * A column the view says nothing about is off. A column whose *type* has since
 * changed keeps whatever was stored -- `field_type` records the type the value
 * was written for, and the server is the one that decides what to do about the
 * mismatch, so throwing the value away here would lose something it might still
 * accept.
 */
export function draftsFrom(
  stored: readonly ViewDefaultValue[],
  field: Field,
  toDraft: (value: unknown) => string,
): DefaultValueDraft {
  const held = stored.find((entry) => entry.field === field.id)
  if (held === undefined) {
    return { fieldId: field.id, enabled: false, draft: toDraft(null), function: null }
  }

  return {
    fieldId: field.id,
    enabled: held.enabled ?? true,
    draft: toDraft(held.value ?? null),
    function: (held.function as DefaultValueFunction | null) ?? null,
  }
}

/**
 * The body the endpoint takes.
 *
 * Every column is sent, disabled ones included: the request replaces the whole
 * set, so a column left out is a default removed -- and a reader who turned one
 * off expects it to stay off rather than to have said nothing about it.
 *
 * A function and a value never travel together. The backend reads the function
 * first, so sending both would store a value nothing will ever look at, and it
 * would come back to a reader as the default they had set before they switched
 * to `now`.
 */
export function defaultValuesBody(
  drafts: readonly DefaultValueDraft[],
  valueOf: (draft: DefaultValueDraft) => unknown,
): {
  field: number
  enabled: boolean
  value: unknown
  function: DefaultValueFunction | null
}[] {
  return drafts.map((draft) => ({
    field: draft.fieldId,
    enabled: draft.enabled,
    value: draft.function === null ? valueOf(draft) : null,
    function: draft.function,
  }))
}
